/* eslint-disable max-lines */
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { DndProvider, useDrag, useDragLayer, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { toast } from 'react-toastify';
import { Button } from 'reactstrap';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import TaskBoardService from '../../../services/TaskBoard';
import TaskBoardCardModal, { CardComment, CardRecurrence, CardRecurrencePayload } from './TaskBoardCardModal';
import TaskBoardImportModal from './TaskBoardImportModal';
import './task-board.scss';
import { AVATAR_PALETTE, COLUMN_ACCENT_COLORS } from './task-board.constants';

const ITEM_TYPE = 'TASK_BOARD_CARD';

type SortOrder = 'position_asc' | 'position_desc' | 'name_asc' | 'due_date';

const SORT_OPTIONS: { value: SortOrder; label: string }[] = [
  { value: 'position_desc', label: 'Date created (newest first)' },
  { value: 'position_asc', label: 'Date created (oldest first)' },
  { value: 'name_asc', label: 'Card name (alphabetically)' },
  { value: 'due_date', label: 'Due date' },
];

const columnSortStorageKey = (schoolId: string) => `task-board-column-sort-${schoolId}`;

const readColumnSortOrders = (schoolId: string): Record<string, SortOrder> => {
  try {
    const stored = JSON.parse(localStorage.getItem(columnSortStorageKey(schoolId)) || '{}');
    const validOrders = SORT_OPTIONS.map((opt) => opt.value);
    return Object.fromEntries(Object.entries(stored).filter(([, order]) => validOrders.includes(order as SortOrder)));
  } catch {
    return {};
  }
};

const saveColumnSortOrders = (schoolId: string, orders: Record<string, SortOrder>) => {
  try {
    localStorage.setItem(columnSortStorageKey(schoolId), JSON.stringify(orders));
  } catch {
    return;
  }
};

const DEFAULT_LIST_NAMES = [
  'Center Todo',
  'Director Todo',
  'Ed Director Todo',
  'Admin Assistant Todo',
  'Teacher Tickets',
];

type BoardMember = {
  userId: string;
  user?: { id: string; firstName: string; lastName: string } | null;
};

function getMemberInitials(userId: string, boardMembers: BoardMember[]): string {
  const found = boardMembers.find((m) => m.userId === userId);
  if (!found?.user) return userId.slice(0, 2).toUpperCase();
  const first = (found.user.firstName || '').charAt(0).toUpperCase();
  const last = (found.user.lastName || '').charAt(0).toUpperCase();
  return `${first}${last}` || userId.slice(0, 2).toUpperCase();
}

type LabelItem = { text: string; color: string };

type BoardList = {
  id: string;
  name: string;
  position: number;
  isCompletedBucket?: boolean;
  defaultMemberUserIds: string[];
  cards: {
    id: string;
    listId?: string;
    title: string;
    description?: string | null;
    dueDate?: string | null;
    position: number;
    simpleLabels?: string[];
    labelsData?: LabelItem[] | null;
    members?: { userId: string; source?: string }[];
    checklists?: {
      id?: string;
      title: string;
      items: {
        id?: string;
        text: string;
        isDone: boolean;
        position: number;
        assignedUserId?: string | null;
        dueDate?: string | null;
      }[];
    }[];
    attachments?: { id: string; fileName: string; fileMimetype: string; signedUrl: string }[];
    commentsCount?: number;
    recurrence?: CardRecurrence | null;
    isRecurrenceTemplate?: boolean;
    isRecurrenceInstance?: boolean;
  }[];
};

type SelectedCard = {
  id?: string;
  listId?: string;
  title: string;
  description?: string | null;
  dueDate?: string | null;
  simpleLabels?: string[];
  labelsData?: LabelItem[] | null;
  members?: { userId: string; source?: string }[];
  checklists?: {
    id?: string;
    title: string;
    items: { id?: string; text: string; isDone: boolean; position: number }[];
  }[];
  recurrence?: CardRecurrence | null;
  isRecurrenceTemplate?: boolean;
  isRecurrenceInstance?: boolean;
  isNew?: boolean;
};

type BoardPayload = {
  id: string;
  name: string;
  concludedBoardId?: string;
  members?: (BoardMember & { groupName?: string })[];
  lists: BoardList[];
};

type TaskBoardKanbanProps = {
  board: BoardPayload;
  schoolId: string;
  onRefresh: () => Promise<void>;
  onSilentRefresh?: () => Promise<void>;
  onCompleted?: () => void;
  tabBar?: React.ReactNode;
};

type DragItem = { cardId: string };

function arrayMove<T>(arr: T[], from: number, to: number): T[] {
  const next = [...arr];
  const [removed] = next.splice(from, 1);
  next.splice(to, 0, removed);
  return next;
}

function computeDropOnCard(
  sortedLists: BoardList[],
  cardId: string,
  destListId: string,
  overCardId: string,
): { listId: string; index: number } | null {
  const sourceList = sortedLists.find((l) => l.cards.some((c) => c.id === cardId));
  if (!sourceList) return null;
  const destList = sortedLists.find((l) => l.id === destListId);
  if (!destList) return null;

  if (sourceList.id === destList.id) {
    const ids = sourceList.cards.map((c) => c.id);
    const oldIndex = ids.indexOf(cardId);
    const newIndex = ids.indexOf(overCardId);
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return null;
    const reordered = arrayMove(ids, oldIndex, newIndex);
    return { listId: destList.id, index: reordered.indexOf(cardId) };
  }
  const index = destList.cards.findIndex((c) => c.id === overCardId);
  if (index === -1) return null;
  return { listId: destList.id, index };
}

function computeDropOnListBackground(
  sortedLists: BoardList[],
  cardId: string,
  destListId: string,
): { listId: string; index: number } | null {
  const sourceList = sortedLists.find((l) => l.cards.some((c) => c.id === cardId));
  if (!sourceList) return null;
  const destList = sortedLists.find((l) => l.id === destListId);
  if (!destList) return null;

  if (sourceList.id === destList.id) {
    return { listId: destList.id, index: Math.max(0, sourceList.cards.length - 1) };
  }
  return { listId: destList.id, index: destList.cards.length };
}

function KanbanCard({
  card,
  listId,
  sortedLists,
  boardMembers,
  onMove,
  moving,
  onOpenCard,
  onComplete,
}: {
  card: {
    id: string;
    title: string;
    description?: string | null;
    dueDate?: string | null;
    simpleLabels?: string[];
    labelsData?: LabelItem[] | null;
    members?: { userId: string; source?: string }[];
    checklists?: {
      id?: string;
      title: string;
      items: {
        id?: string;
        text: string;
        isDone: boolean;
        position: number;
        assignedUserId?: string | null;
        dueDate?: string | null;
      }[];
    }[];
    attachments?: { id: string; fileName: string; fileMimetype: string; signedUrl: string }[];
    commentsCount?: number;
    isRecurrenceTemplate?: boolean;
    isRecurrenceInstance?: boolean;
  };
  listId: string;
  sortedLists: BoardList[];
  boardMembers: BoardMember[];
  onMove: (cardId: string, listId: string, index: number) => Promise<void>;
  moving: boolean;
  onOpenCard: (card: any) => void;
  onComplete: (cardId: string) => Promise<void>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { cardId: card.id },
    canDrag: () => !moving,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver: isOverCard }, drop] = useDrop<DragItem, { handled?: boolean }, { isOver: boolean }>({
    accept: ITEM_TYPE,
    canDrop: (item) => item.cardId !== card.id,
    collect: (monitor) => ({ isOver: monitor.isOver() && monitor.canDrop() }),
    drop(item) {
      if (item.cardId === card.id) return undefined;
      const payload = computeDropOnCard(sortedLists, item.cardId, listId, card.id);
      if (!payload) return { handled: true };
      void onMove(item.cardId, payload.listId, payload.index);
      return { handled: true };
    },
  });

  drag(drop(ref));

  const descriptionPreview = (card.description || '').trim();
  const previewText =
    descriptionPreview.length > 96 ? `${descriptionPreview.slice(0, 96).trimEnd()}...` : descriptionPreview;

  const dueBadge = (() => {
    if (!card.dueDate) return null;
    const due = moment(String(card.dueDate).slice(0, 10), 'YYYY-MM-DD');
    if (!due.isValid()) return { label: card.dueDate, variant: 'muted' };
    const today = moment().startOf('day');
    const dueDay = due.clone().startOf('day');
    const label = due.format('MMM DD');
    if (dueDay.isBefore(today)) return { label: `Overdue: ${label}`, variant: 'danger' };
    if (dueDay.isSame(today)) return { label: 'Due today', variant: 'warning' };
    return { label, variant: 'success' };
  })();

  const checklistTotals = (card.checklists || []).reduce(
    (acc, checklist) => {
      const items = checklist.items || [];
      acc.total += items.length;
      acc.done += items.filter((item) => item.isDone).length;
      return acc;
    },
    { total: 0, done: 0 },
  );

  const cardLabels = card.simpleLabels || [];
  const cardMembers = (card.members || []).slice(0, 3);

  return (
    <div
      ref={ref}
      className={`task-board__card ${isDragging ? 'task-board__card--dragging' : ''}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      onClick={() => onOpenCard(card)}
    >
      {isOverCard && <div className="task-board__card-drop-indicator" />}
      <button
        type="button"
        className="task-board__card-complete-btn"
        title="Mark as completed"
        disabled={moving}
        onClick={(e) => {
          e.stopPropagation();
          void onComplete(card.id);
        }}
      >
        <i className="fas fa-check" />
      </button>

      {cardLabels.length > 0 && (
        <div className="task-board__card-labels-row">
          {cardLabels.slice(0, 3).map((label) => (
            <span key={label} className="task-board__card-label task-board__card-label--simple" title={label}>
              {label}
            </span>
          ))}
          {cardLabels.length > 3 && (
            <span className="task-board__card-label task-board__card-label--more">+{cardLabels.length - 3}</span>
          )}
        </div>
      )}
      {(card.labelsData || []).length > 0 && (
        <div className="task-board__card-labels-row">
          {(card.labelsData || []).slice(0, 4).map((label, idx) => (
            <span
              key={idx}
              className="task-board__card-label"
              style={{ backgroundColor: label.color || '#6b7280', color: '#fff' }}
              title={label.text}
            >
              {label.text}
            </span>
          ))}
          {(card.labelsData || []).length > 4 && (
            <span className="task-board__card-label task-board__card-label--more">
              +{(card.labelsData || []).length - 4}
            </span>
          )}
        </div>
      )}

      <div className="task-board__card-title">{card.title}</div>

      {!!previewText && <div className="task-board__card-desc">{previewText}</div>}

      <div className="task-board__card-footer">
        <div className="task-board__card-meta">
          {dueBadge && (
            <span className={`task-board__card-pill task-board__card-pill--${dueBadge.variant}`}>
              <i className="far fa-calendar-alt" />
              {dueBadge.label}
            </span>
          )}
          {checklistTotals.total > 0 && (
            <span className="task-board__card-pill task-board__card-pill--info">
              <i className="fas fa-check-square" />
              {checklistTotals.done}/{checklistTotals.total}
            </span>
          )}
          {card.isRecurrenceTemplate && (
            <span className="task-board__card-pill task-board__card-pill--info" title="Recurring card">
              <i className="fas fa-redo" />
            </span>
          )}
          {card.isRecurrenceInstance && (
            <span
              className="task-board__card-pill task-board__card-pill--neutral"
              title="Generated from a recurring card"
            >
              <i className="fas fa-redo" />
            </span>
          )}
          {!!(card.attachments || []).length && (
            <span className="task-board__card-pill task-board__card-pill--neutral">
              <i className="fas fa-paperclip" />
              {(card.attachments || []).length}
            </span>
          )}
          {!!card.commentsCount && (
            <span className="task-board__card-pill task-board__card-pill--neutral">
              <i className="far fa-comment-alt" />
              {card.commentsCount}
            </span>
          )}
        </div>

        <div className="task-board__card-footer-right">
          {cardMembers.length > 0 && (
            <div className="task-board__card-avatars">
              {cardMembers.map((m, i) => {
                const palette = AVATAR_PALETTE[i % AVATAR_PALETTE.length];
                return (
                  <span
                    key={m.userId}
                    className="task-board__card-avatar"
                    style={{ backgroundColor: palette.bg, color: palette.color }}
                    title={getMemberInitials(m.userId, boardMembers)}
                  >
                    {getMemberInitials(m.userId, boardMembers)}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ListColumn({
  list,
  accentColor,
  boardMembers,
  sortedLists,
  onAddCard,
  onMove,
  moving,
  onOpenCard,
  onComplete,
  onRenameList,
  onDeleteList,
  sortOrder,
  onSortChange,
}: {
  list: BoardList;
  accentColor: string;
  boardMembers: BoardMember[];
  sortedLists: BoardList[];
  onAddCard: (listId: string) => void;
  onMove: (cardId: string, listId: string, index: number) => Promise<void>;
  moving: boolean;
  onOpenCard: (card: any) => void;
  onComplete: (cardId: string) => Promise<void>;
  onRenameList: (listId: string, title: string) => Promise<void>;
  onDeleteList: (listId: string) => Promise<void>;
  sortOrder: SortOrder;
  onSortChange: (listId: string, order: SortOrder) => void;
}) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);
  const committingRef = useRef(false);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerValue, setHeaderValue] = useState(list.name);
  const [sortPopupOpen, setSortPopupOpen] = useState(false);
  const sortWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sortPopupOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (sortWrapperRef.current && !sortWrapperRef.current.contains(e.target as Node)) {
        setSortPopupOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sortPopupOpen]);

  const { isDraggingAny } = useDragLayer((monitor) => ({
    isDraggingAny: monitor.isDragging() && monitor.getItemType() === ITEM_TYPE,
  }));

  const [{ isOver: isOverColumn }, drop] = useDrop<DragItem, { handled?: boolean }, { isOver: boolean }>({
    accept: ITEM_TYPE,
    collect: (monitor) => ({ isOver: monitor.isOver() }),
    drop(item, monitor) {
      if (monitor.didDrop()) return undefined;
      const payload = computeDropOnListBackground(sortedLists, item.cardId, list.id);
      if (!payload) return undefined;
      void onMove(item.cardId, payload.listId, payload.index);
      return { handled: true };
    },
  });

  drop(bodyRef);

  const startEditHeader = () => {
    setHeaderValue(list.name);
    setEditingHeader(true);
    setTimeout(() => headerInputRef.current?.select(), 0);
  };

  const commitHeader = async () => {
    if (committingRef.current) return;
    committingRef.current = true;
    setEditingHeader(false);
    const trimmed = headerValue.trim();
    try {
      if (trimmed && trimmed !== list.name) {
        await onRenameList(list.id, trimmed);
      } else {
        setHeaderValue(list.name);
      }
    } finally {
      committingRef.current = false;
    }
  };

  const cancelHeader = () => {
    setEditingHeader(false);
    setHeaderValue(list.name);
  };

  return (
    <div className="task-board__column">
      <div className="task-board__column-accent" style={{ backgroundColor: accentColor }} />
      <div className="task-board__column-header">
        {editingHeader ? (
          <input
            ref={headerInputRef}
            value={headerValue}
            className="task-board__column-title-input"
            onChange={(e) => setHeaderValue(e.target.value)}
            onBlur={() => void commitHeader()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void commitHeader();
              if (e.key === 'Escape') cancelHeader();
            }}
          />
        ) : (
          <>
            <span
              className="task-board__column-title"
              title="Click to rename"
              onClick={moving ? undefined : startEditHeader}
            >
              {list.name}
            </span>
            <span className="task-board__column-count">{list.cards.length}</span>
            <div ref={sortWrapperRef} className="task-board__column-sort-wrapper">
              <button
                type="button"
                className={`task-board__column-sort-btn${
                  sortOrder !== 'position_asc' ? ' task-board__column-sort-btn--active' : ''
                }`}
                title="Sort cards"
                onClick={(e) => {
                  e.stopPropagation();
                  setSortPopupOpen((v) => !v);
                }}
              >
                <i className="fas fa-ellipsis-h" />
              </button>
              {sortPopupOpen && (
                <div className="task-board__column-sort-popup">
                  <div className="task-board__column-sort-popup-header">
                    <span className="task-board__column-sort-popup-title">Sort list</span>
                    <button
                      type="button"
                      className="task-board__column-sort-popup-close"
                      onClick={() => setSortPopupOpen(false)}
                    >
                      <i className="fas fa-times" />
                    </button>
                  </div>
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`task-board__column-sort-popup-option${
                        sortOrder === opt.value ? ' task-board__column-sort-popup-option--active' : ''
                      }`}
                      onClick={() => {
                        onSortChange(list.id, opt.value);
                        setSortPopupOpen(false);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {list.cards.length === 0 && !DEFAULT_LIST_NAMES.includes(list.name) && (
              <button
                type="button"
                className="task-board__column-delete-btn"
                title="Remove empty list"
                disabled={moving}
                onClick={(e) => {
                  e.stopPropagation();
                  void onDeleteList(list.id);
                }}
              >
                <i className="fas fa-trash-alt" />
              </button>
            )}
          </>
        )}
      </div>
      <div
        ref={bodyRef}
        className={`task-board__column-body${
          isDraggingAny && !isOverColumn ? ' task-board__column-body--drag-possible' : ''
        }${isOverColumn ? ' task-board__column-body--drag-over' : ''}`}
      >
        {list.cards.map((card) => (
          <KanbanCard
            key={card.id}
            card={card}
            listId={list.id}
            sortedLists={sortedLists}
            boardMembers={boardMembers}
            onMove={onMove}
            moving={moving}
            onOpenCard={onOpenCard}
            onComplete={onComplete}
          />
        ))}
      </div>
      <div className="task-board__column-footer">
        <button type="button" className="task-board__footer-btn" disabled={moving} onClick={() => onAddCard(list.id)}>
          + Add a card
        </button>
      </div>
    </div>
  );
}

const TaskBoardKanban = ({
  board,
  schoolId,
  onRefresh,
  onSilentRefresh,
  onCompleted,
  tabBar,
}: TaskBoardKanbanProps) => {
  const { token } = useContext(AuthContext);
  const [moving, setMoving] = useState(false);
  const [changingList, setChangingList] = useState(false);
  const [selectedCard, setSelectedCard] = useState<SelectedCard | null>(null);
  const [columnSortOrders, setColumnSortOrders] = useState<Record<string, SortOrder>>(() =>
    readColumnSortOrders(schoolId),
  );
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [addingList, setAddingList] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');
  const newListInputRef = useRef<HTMLInputElement>(null);
  const [comments, setComments] = useState<CardComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);

  useEffect(() => {
    if (!selectedCard?.id || (selectedCard as any).isNew || !token) {
      setComments([]);
      return;
    }
    setLoadingComments(true);
    TaskBoardService(token)
      .listComments(schoolId, selectedCard.id)
      .then((result) => setComments(result?.data ?? result ?? []))
      .catch(() => toast.error('Could not load comments.'))
      .finally(() => setLoadingComments(false));
  }, [selectedCard?.id]);

  const sortedLists = useMemo(() => {
    const sortCards = (cards: BoardList['cards'], order: SortOrder) => {
      const sorted = [...cards];
      switch (order) {
        case 'position_desc':
          return sorted.sort((a, b) => b.position - a.position);
        case 'name_asc':
          return sorted.sort((a, b) => a.title.localeCompare(b.title));
        case 'due_date':
          return sorted.sort((a, b) => {
            if (!a.dueDate && !b.dueDate) return 0;
            if (!a.dueDate) return 1;
            if (!b.dueDate) return -1;
            return moment(String(a.dueDate)).valueOf() - moment(String(b.dueDate)).valueOf();
          });
        case 'position_asc':
        default:
          return sorted.sort((a, b) => a.position - b.position);
      }
    };

    return [...board.lists]
      .sort((a, b) => a.position - b.position)
      .map((list) => ({
        ...list,
        cards: sortCards(list.cards, columnSortOrders[list.id] || 'position_asc'),
      }));
  }, [board.lists, columnSortOrders]);

  const onMove = async (cardId: string, listId: string, index: number) => {
    if (!token) return;
    setMoving(true);
    try {
      await TaskBoardService(token).moveCard(schoolId, cardId, { listId, index });
      await onRefresh();
    } catch (e) {
      toast.error('Could not move card.');
    } finally {
      setMoving(false);
    }
  };

  const onChangeCardList = async (listId: string): Promise<boolean> => {
    if (!token || !selectedCard?.id) return false;
    const destList = sortedLists.find((l) => l.id === listId);
    if (!destList) return false;
    setChangingList(true);
    try {
      await TaskBoardService(token).moveCard(schoolId, selectedCard.id, { listId, index: destList.cards.length });
      await (onSilentRefresh ?? onRefresh)();
      toast.success(`Card moved to ${destList.name}.`);
      return true;
    } catch (e) {
      toast.error('Could not move card.');
      return false;
    } finally {
      setChangingList(false);
    }
  };

  const onAddCard = (listId: string) => {
    const list = sortedLists.find((l) => l.id === listId);
    const previewAutoMembers = (list?.defaultMemberUserIds || []).map((uid) => ({ userId: uid, source: 'auto' }));
    setSelectedCard({
      listId,
      title: '',
      description: '',
      dueDate: null,
      simpleLabels: [],
      members: previewAutoMembers,
      checklists: [],
      isNew: true,
    });
  };

  const onAddList = async () => {
    if (!token) return;
    const title = newListTitle.trim() || 'New list';
    setMoving(true);
    try {
      await TaskBoardService(token).createList(schoolId, { title });
      await onRefresh();
      setAddingList(false);
      setNewListTitle('');
    } catch (e) {
      toast.error('Could not create list.');
    } finally {
      setMoving(false);
    }
  };

  const onRenameList = async (listId: string, title: string) => {
    if (!token) return;
    try {
      await TaskBoardService(token).renameList(schoolId, listId, title);
      await onRefresh();
    } catch (e) {
      toast.error('Could not rename list.');
    }
  };

  const onDeleteList = async (listId: string) => {
    if (!token) return;
    if (!window.confirm('Remove this empty list?')) return;
    setMoving(true);
    try {
      await TaskBoardService(token).deleteList(schoolId, listId);
      await onRefresh();
    } catch (e) {
      toast.error('Could not remove list.');
    } finally {
      setMoving(false);
    }
  };

  const openAddList = () => {
    setAddingList(true);
    setNewListTitle('');
    setTimeout(() => newListInputRef.current?.focus(), 0);
  };

  const onCompleteCard = async (cardId: string) => {
    if (!token) return;
    setMoving(true);
    try {
      await TaskBoardService(token).completeCard(schoolId, cardId);
      toast.success('Card moved to the concluded board.');
      await onRefresh();
      onCompleted?.();
    } catch {
      toast.error('Could not complete card.');
    } finally {
      setMoving(false);
    }
  };

  const onUploadAttachment = async (file: File) => {
    if (!token || !selectedCard?.id) return null;
    try {
      const formData = new FormData();
      formData.append('attachment', file);
      const result = await TaskBoardService(token).uploadAttachment(schoolId, selectedCard.id, formData);
      return result?.data ?? null;
    } catch (e: any) {
      const role = e?.response?.data?.errors?.role;
      const maxSize = e?.response?.data?.errors?.maxSize;
      const apiMessage = e?.response?.data?.message;
      if (role === 'FILE_SIZE') {
        const limitMb = maxSize ? `${(Number(maxSize) / 1_000_000).toFixed(0)} MB` : null;
        toast.error(`File too large.${limitMb ? ` Maximum size is ${limitMb}.` : ''}`);
      } else if (role === 'MIMETYPE') {
        toast.error('File type not allowed. Accepted: PNG, JPG, PDF, XLSX.');
      } else {
        toast.error(apiMessage || 'Could not upload attachment.');
      }
      return null;
    }
  };

  const onDeleteAttachment = async (attachmentId: string) => {
    if (!token || !selectedCard?.id) return;
    try {
      await TaskBoardService(token).deleteAttachment(schoolId, selectedCard.id, attachmentId);
    } catch {
      toast.error('Could not delete attachment.');
    }
  };

  const onAddComment = async (text: string): Promise<CardComment | null> => {
    if (!token || !selectedCard?.id) return null;
    try {
      const result = await TaskBoardService(token).createComment(schoolId, selectedCard.id, text);
      const comment: CardComment = result?.data ?? result;
      setComments((prev) => [...prev, comment]);
      return comment;
    } catch {
      toast.error('Could not add comment.');
      return null;
    }
  };

  const onMarkCompleted = async () => {
    if (!token || !selectedCard?.id) return;
    setMoving(true);
    try {
      await TaskBoardService(token).completeCard(schoolId, selectedCard.id);
      setSelectedCard(null);
      toast.success('Card moved to the concluded board.');
      await onRefresh();
      onCompleted?.();
    } catch {
      toast.error('Could not complete card.');
    } finally {
      setMoving(false);
    }
  };

  const isSameRecurrence = (current: CardRecurrence, next: CardRecurrencePayload): boolean => {
    const sameEndDate =
      (current.endDate ? String(current.endDate).slice(0, 10) : null) ===
      (next.endDate ? String(next.endDate).slice(0, 10) : null);
    const sameMaxOccurrences = (current.maxOccurrences ?? null) === (next.maxOccurrences ?? null);
    const sameSkipWeekends = !!current.skipWeekends === !!next.skipWeekends;
    return (
      current.isActive &&
      current.frequency === next.frequency &&
      Number(current.interval) === Number(next.interval) &&
      current.endType === next.endType &&
      sameEndDate &&
      sameMaxOccurrences &&
      sameSkipWeekends
    );
  };

  const syncCardRecurrence = async (
    cardId: string,
    current: CardRecurrence | null,
    next: CardRecurrencePayload | null,
  ) => {
    if (!token) return;
    if (next && !current) {
      await TaskBoardService(token).createCardRecurrence(schoolId, cardId, next);
      return;
    }
    if (next && current) {
      if (isSameRecurrence(current, next)) return;
      await TaskBoardService(token).updateCardRecurrence(schoolId, cardId, next);
      return;
    }
    if (!next && current) {
      await TaskBoardService(token).deleteCardRecurrence(schoolId, cardId);
    }
  };

  const onSaveCard = async (payload: {
    title: string;
    description: string | null;
    dueDate: string | null;
    simpleLabels: string[];
    labelsData: LabelItem[] | null;
    memberUserIds: string[];
    checklists: {
      id?: string;
      title: string;
      items: {
        id?: string;
        text: string;
        isDone: boolean;
        position: number;
        assignedUserId?: string | null;
        dueDate?: string | null;
      }[];
    }[];
    recurrence: CardRecurrencePayload | null;
  }) => {
    if (!token) return;
    setMoving(true);
    const { recurrence, ...cardPayload } = payload;
    try {
      if (selectedCard?.isNew) {
        if (!selectedCard.listId) {
          toast.error('Could not create card.');
          return;
        }
        const createResult = await TaskBoardService(token).createCard(schoolId, {
          listId: selectedCard.listId,
          title: payload.title,
        });
        const createdCardId = createResult?.data?.id || createResult?.id;
        if (!createdCardId) {
          toast.error('Could not create card.');
          return;
        }
        await TaskBoardService(token).updateCard(schoolId, createdCardId, cardPayload);
        if (recurrence) {
          try {
            await TaskBoardService(token).createCardRecurrence(schoolId, createdCardId, recurrence);
          } catch {
            toast.error('Card created, but recurrence could not be set.');
          }
        }
        toast.success('Card created.');
      } else if (selectedCard?.id) {
        await TaskBoardService(token).updateCard(schoolId, selectedCard.id, cardPayload);
        try {
          await syncCardRecurrence(selectedCard.id, selectedCard.recurrence ?? null, recurrence);
        } catch {
          toast.error('Card updated, but recurrence could not be saved.');
        }
        toast.success('Card updated.');
      } else {
        toast.error('Could not update card.');
        return;
      }
      setSelectedCard(null);
      await (onSilentRefresh ?? onRefresh)();
    } catch {
      toast.error(selectedCard?.isNew ? 'Could not create card.' : 'Could not update card.');
    } finally {
      setMoving(false);
    }
  };

  return (
    <>
      <div className="task-board__board-header">
        <h2 className="task-board__board-title">{board.name}</h2>
      </div>
      <div className="task-board__tab-bar-row">
        {tabBar}
        <Button
          color="secondary"
          outline
          size="sm"
          onClick={() => setIsImportOpen(true)}
          disabled={moving}
          className="mb-2"
        >
          Import Tasks
        </Button>
      </div>

      <DndProvider backend={HTML5Backend}>
        <div className="task-board__scroll">
          {sortedLists.map((list, index) => (
            <ListColumn
              key={list.id}
              list={list}
              accentColor={COLUMN_ACCENT_COLORS[index % COLUMN_ACCENT_COLORS.length]}
              boardMembers={board.members || []}
              sortedLists={sortedLists}
              onAddCard={onAddCard}
              onMove={onMove}
              moving={moving}
              onOpenCard={setSelectedCard}
              onComplete={onCompleteCard}
              onRenameList={onRenameList}
              onDeleteList={onDeleteList}
              sortOrder={columnSortOrders[list.id] || 'position_asc'}
              onSortChange={(listId, order) => {
                const next = { ...columnSortOrders, [listId]: order };
                setColumnSortOrders(next);
                saveColumnSortOrders(schoolId, next);
              }}
            />
          ))}
          <div className="task-board__add-list">
            {addingList ? (
              <div className="task-board__column" style={{ padding: '0.625rem' }}>
                <input
                  ref={newListInputRef}
                  value={newListTitle}
                  onChange={(e) => setNewListTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void onAddList();
                    if (e.key === 'Escape') {
                      setAddingList(false);
                      setNewListTitle('');
                    }
                  }}
                  placeholder="List name…"
                  className="task-board__new-list-input"
                />
                <div className="d-flex" style={{ gap: '0.5rem' }}>
                  <Button color="primary" size="sm" disabled={moving} onClick={() => void onAddList()}>
                    Add list
                  </Button>
                  <Button
                    color="secondary"
                    outline
                    size="sm"
                    onClick={() => {
                      setAddingList(false);
                      setNewListTitle('');
                    }}
                  >
                    ✕
                  </Button>
                </div>
              </div>
            ) : (
              <button type="button" className="task-board__add-list-btn" disabled={moving} onClick={openAddList}>
                + Add another list
              </button>
            )}
          </div>
        </div>
      </DndProvider>

      <TaskBoardCardModal
        isOpen={!!selectedCard}
        card={selectedCard as any}
        boardMembers={board.members || []}
        saving={moving}
        onClose={() => setSelectedCard(null)}
        onSave={onSaveCard}
        onUploadAttachment={selectedCard?.isNew ? undefined : onUploadAttachment}
        onDeleteAttachment={selectedCard?.isNew ? undefined : onDeleteAttachment}
        onMarkCompleted={selectedCard?.isNew ? undefined : onMarkCompleted}
        lists={sortedLists.map((list) => ({ id: list.id, name: list.name }))}
        onChangeList={selectedCard?.isNew ? undefined : onChangeCardList}
        changingList={changingList}
        comments={comments}
        loadingComments={loadingComments}
        onAddComment={selectedCard?.isNew ? undefined : onAddComment}
      />
      {!!token && (
        <TaskBoardImportModal
          isOpen={isImportOpen}
          onClose={() => setIsImportOpen(false)}
          token={token}
          schoolId={schoolId}
          lists={sortedLists.map((list) => ({ id: list.id, name: list.name }))}
          members={board.members || []}
          onRefresh={onRefresh}
        />
      )}
    </>
  );
};

export default TaskBoardKanban;
